import React from "react";

const Privacy = () => {
    return (
        <div className="min-h-screen bg-slate-950 text-slate-300 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto bg-slate-900/50 backdrop-blur-md rounded-2xl p-8 border border-slate-700 shadow-xl">
                <h1 className="text-3xl font-bold mb-8 text-white border-b border-slate-700 pb-4">
                    Privacy Policy
                </h1>

                <div className="prose prose-invert prose-lg max-w-none">
                    <p className="text-slate-400 mb-4">
                        <strong>Last Updated:</strong> December 2024
                    </p>

                    <h2 className="text-xl font-semibold mt-6 mb-2 text-wurkzi-400">
                        1. Introduction
                    </h2>
                    <p className="text-slate-400 mb-4">
                        Wurkzi ("we," "our," or "us") is committed to protecting your
                        privacy. This Privacy Policy explains how we collect, use, disclose,
                        and safeguard your information when you use our platform.
                    </p>

                    <hr className="my-6 border-slate-700" />

                    <h2 className="text-xl font-semibold mt-6 mb-2 text-wurkzi-400">
                        2. Information We Collect
                    </h2>

                    <h3 className="text-lg font-semibold mt-4 mb-2 text-white">
                        Personal Information
                    </h3>
                    <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-400">
                        <li>Name and contact information (email, phone number)</li>
                        <li>Date of birth (to verify age eligibility)</li>
                        <li>Account credentials</li>
                        <li>Payment information (processed securely via Stripe)</li>
                        <li>Profile information (skills, availability, location)</li>
                    </ul>

                    <h3 className="text-lg font-semibold mt-4 mb-2 text-white">
                        Usage Information
                    </h3>
                    <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-400">
                        <li>Device information (browser type, operating system)</li>
                        <li>Log data (IP address, access times, pages viewed)</li>
                        <li>Job history and communications on the platform</li>
                    </ul>

                    <h3 className="text-lg font-semibold mt-4 mb-2 text-white">
                        SMS and Notification Information
                    </h3>
                    <p className="text-slate-400 mb-4">
                        If you provide your phone number and opt in to SMS alerts, Wurkzi
                        collects your phone number, SMS consent status, and related consent
                        records. Wurkzi uses this information to send transactional alerts
                        about applications, job updates, and messages. You can opt out at any
                        time by replying STOP. Message and data rates may apply. You may
                        reply START to resume SMS alerts.
                    </p>
                    <p className="text-slate-400 mb-4">
                        We do not sell or share your SMS opt-in data or personal information
                        with third parties for marketing purposes.
                    </p>

                    <hr className="my-6 border-slate-700" />

                    <h2 className="text-xl font-semibold mt-6 mb-2 text-wurkzi-400">
                        3. How We Use Your Information
                    </h2>
                    <p className="text-slate-400 mb-2">We use your information to:</p>
                    <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-400">
                        <li>Create and manage your account</li>
                        <li>Connect teen workers with clients</li>
                        <li>Process payments securely</li>
                        <li>Communicate about jobs, updates, and support</li>
                        <li>Improve our services and user experience</li>
                        <li>Ensure platform safety and prevent fraud</li>
                        <li>Comply with legal obligations</li>
                    </ul>

                    <hr className="my-6 border-slate-700" />

                    <h2 className="text-xl font-semibold mt-6 mb-2 text-wurkzi-400">
                        4. Information Sharing
                    </h2>
                    <p className="text-slate-400 mb-2">
                        We may share your information with:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-400">
                        <li>
                            <strong>Other Users:</strong> Clients and workers can see
                            relevant profile information to facilitate jobs
                        </li>
                        <li>
                            <strong>Service Providers:</strong> Third-party services that help
                            us operate our platform (payment processing, hosting)
                        </li>
                        <li>
                            <strong>Legal Requirements:</strong> When required by law or to
                            protect our rights
                        </li>
                    </ul>
                    <p className="text-slate-400 mb-4">
                        We do <strong>not</strong> sell your personal information to third
                        parties.
                    </p>

                    <hr className="my-6 border-slate-700" />

                    <h2 className="text-xl font-semibold mt-6 mb-2 text-wurkzi-400">
                        5. Data Security
                    </h2>
                    <p className="text-slate-400 mb-4">
                        We implement appropriate technical and organizational security
                        measures to protect your information. However, no method of
                        transmission over the Internet is 100% secure, and we cannot
                        guarantee absolute security.
                    </p>

                    <hr className="my-6 border-slate-700" />

                    <h2 className="text-xl font-semibold mt-6 mb-2 text-wurkzi-400">
                        6. Children's Privacy
                    </h2>
                    <p className="text-slate-400 mb-4">
                        Our platform is designed for teen workers aged 13-17, and we require
                        parental consent for minors. Parents/guardians have the right to
                        review, delete, or restrict the collection of their child's
                        information. Contact us at support@wurkzi.com to exercise these
                        rights.
                    </p>

                    <hr className="my-6 border-slate-700" />

                    <h2 className="text-xl font-semibold mt-6 mb-2 text-wurkzi-400">
                        7. Your Rights
                    </h2>
                    <p className="text-slate-400 mb-2">You have the right to:</p>
                    <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-400">
                        <li>Access your personal information</li>
                        <li>Correct inaccurate data</li>
                        <li>Request deletion of your data</li>
                        <li>Opt-out of marketing communications</li>
                        <li>Request data portability</li>
                    </ul>

                    <hr className="my-6 border-slate-700" />

                    <h2 className="text-xl font-semibold mt-6 mb-2 text-wurkzi-400">
                        8. Cookies and Tracking
                    </h2>
                    <p className="text-slate-400 mb-4">
                        We use cookies and similar technologies to enhance your experience,
                        analyze usage, and provide personalized content. You can manage
                        cookie preferences through your browser settings.
                    </p>

                    <hr className="my-6 border-slate-700" />

                    <h2 className="text-xl font-semibold mt-6 mb-2 text-wurkzi-400">
                        9. Changes to This Policy
                    </h2>
                    <p className="text-slate-400 mb-4">
                        We may update this Privacy Policy from time to time. We will notify
                        you of any material changes by posting the new policy on this page
                        and updating the "Last Updated" date.
                    </p>

                    <hr className="my-6 border-slate-700" />

                    <h2 className="text-xl font-semibold mt-6 mb-2 text-wurkzi-400">
                        10. Contact Us
                    </h2>
                    <p className="text-slate-400 mb-4">
                        If you have any questions about this Privacy Policy, please contact
                        us at:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-400">
                        <li>Email: support@wurkzi.com</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default Privacy;
